import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { toast } from 'sonner';
import axios from 'axios';
import Layout from '../components/Layout';
import { Loader2, CheckCircle2, AlertCircle, Upload as UploadIcon, Copy } from 'lucide-react';

const Upload = () => {
  const [searchParams] = useSearchParams();
  const regId = searchParams.get('id');
  
  const [file, setFile] = useState(null);
  const [utr, setUtr] = useState("");
  const [status, setStatus] = useState("PENDING"); // PENDING, UPLOADING, VERIFIED, NEEDS_REVIEW
  const [details, setDetails] = useState(null);
  const [regData, setRegData] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // Fetch Settings
    axios.get(`${BACKEND_URL}/api/settings`)
      .then(res => setSettings(res.data))
      .catch(err => console.error(err));

    if (regId) {
      // Fetch user name etc
      axios.get(`${BACKEND_URL}/api/registration/${regId}`)
        .then(res => {
          setRegData(res.data);
          if (res.data.payment_status === 'VERIFIED') setStatus('VERIFIED');
          if (res.data.payment_status === 'NEEDS_REVIEW') setStatus('NEEDS_REVIEW');
        })
        .catch(err => console.error(err));
    }
  }, [regId]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please select a file");

    setStatus("UPLOADING");
    const formData = new FormData();
    formData.append("registration_id", regId);
    formData.append("file", file);
    if (utr) formData.append("utr_entered", utr);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setStatus(res.data.status); // VERIFIED or NEEDS_REVIEW
      setDetails(res.data.details);
      
      if (res.data.status === "VERIFIED") {
        toast.success("Payment Verified Successfully!");
      } else {
        toast.warning("Payment uploaded but needs review.");
      }
    } catch (error) {
      setStatus("PENDING");
      toast.error("Upload failed. Please try again.");
    }
  };

  if (!regId) return <Layout><div className="p-10 text-center">Invalid Link</div></Layout>;
  if (!settings) return <Layout><div className="p-10 text-center">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Bank Details */}
          <div className="bg-white p-6 rounded shadow border border-border h-fit">
            <h2 className="text-2xl font-bold text-primary mb-4">Payment Instructions</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>Please transfer <strong>₹{settings.fee}</strong> to:</p>
              
              <div className="bg-secondary/10 p-4 rounded border border-secondary/20 space-y-2">
                <div>
                  <label className="text-xs uppercase tracking-wider opacity-70">Account Number</label>
                  <div className="font-mono text-lg font-bold text-foreground flex items-center gap-2">
                    {settings.bank_account_no}
                    <Copy className="w-4 h-4 cursor-pointer hover:text-primary" onClick={() => {navigator.clipboard.writeText(settings.bank_account_no); toast.success("Copied")}}/>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider opacity-70">IFSC Code</label>
                  <div className="font-mono text-lg font-bold text-foreground">{settings.bank_ifsc}</div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider opacity-70">Name</label>
                  <div className="font-bold text-foreground">{settings.bank_name}</div>
                </div>
              </div>

              <div className="bg-primary/5 p-4 rounded border border-primary/10">
                <p className="text-sm font-semibold">Or UPI to:</p>
                <p className="font-mono text-lg font-bold text-primary mt-1">{settings.upi_id}</p>
                <p className="text-xs text-muted-foreground mt-1">
                   (For manual queries/confirmation: {settings.support_phone})
                </p>
              </div>
            </div>
          </div>

          {/* Upload Section */}
          <div className="bg-white p-6 rounded shadow-xl border-t-4 border-secondary temple-border">
             {status === "VERIFIED" ? (
               <div className="text-center py-10 space-y-4 animate-fade-in">
                 <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto" />
                 <h2 className="text-3xl font-bold text-green-700">Payment Verified!</h2>
                 <p className="text-muted-foreground">
                   Thank you {regData?.full_name}. You have been successfully registered. 
                   Check your email for further details.
                 </p>
               </div>
             ) : status === "NEEDS_REVIEW" ? (
               <div className="text-center py-10 space-y-4 animate-fade-in">
                 <AlertCircle className="w-20 h-20 text-orange-500 mx-auto" />
                 <h2 className="text-2xl font-bold text-orange-600">Under Review</h2>
                 <p className="text-muted-foreground">
                   We received your screenshot but couldn't auto-verify details. 
                   Our team will check and approve manually.
                 </p>
                 {details?.match_reason && (
                    <div className="bg-orange-50 text-orange-800 text-sm p-3 rounded mt-2">
                        Issue: {details.match_reason}
                    </div>
                 )}
               </div>
             ) : (
               <div className="space-y-6">
                 <h2 className="text-2xl font-bold text-primary">Upload Screenshot</h2>
                 <p className="text-sm text-muted-foreground">
                   After payment, upload the confirmation screenshot here.
                 </p>

                 <form onSubmit={handleUpload} className="space-y-4">
                   
                   <div className="space-y-2">
                     <label className="text-sm font-medium">Screenshot (JPG/PNG/PDF) *</label>
                     <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:bg-secondary/5 transition-colors cursor-pointer relative">
                       <input 
                         type="file" 
                         accept="image/*,application/pdf"
                         onChange={(e) => setFile(e.target.files[0])}
                         className="absolute inset-0 opacity-0 cursor-pointer"
                         required
                       />
                       <UploadIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                       <p className="text-sm text-muted-foreground">
                         {file ? <span className="text-primary font-semibold">{file.name}</span> : "Click to browse or drag file here"}
                       </p>
                     </div>
                   </div>

                   <div className="space-y-2">
                     <label className="text-sm font-medium">UTR / Transaction ID (Optional)</label>
                     <input 
                       value={utr}
                       onChange={e => setUtr(e.target.value)}
                       className="w-full border p-2 rounded outline-none focus:border-primary font-mono text-sm"
                       placeholder="e.g. 123456789012"
                     />
                     <p className="text-xs text-muted-foreground">Providing this helps in faster verification.</p>
                   </div>

                   <button 
                     type="submit"
                     disabled={status === "UPLOADING"}
                     className="w-full bg-primary text-white py-3 rounded font-semibold shadow hover:bg-primary/90 transition-all flex justify-center items-center gap-2"
                   >
                     {status === "UPLOADING" ? <><Loader2 className="animate-spin" /> Verifying...</> : "Verify Payment"}
                   </button>
                 </form>
               </div>
             )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Upload;
